import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "api/config/axiosConfig";
import type { Category, UpdateCategoryRequest } from "api/config/chalice-types";
import { genericError } from "fixtures/globalConstants";
import { useAppSelector } from "store";
import { currentBusinessGetter } from "store/user/userSlice";

// Input type for creation: Full Category schema minus server-generated fields and business_id (added internally)
type CreateCategoryInput = Omit<Category, "id" | "created_at" | "business_id">;

const CATEGORIES_QUERY_KEY = "categories";

export const useCategories = () => {
  const queryClient = useQueryClient();
  const currentBusiness = useAppSelector(currentBusinessGetter);
  const businessId = currentBusiness?.id;

  // --- Fetch Categories Query ---
  const { data: categoriesData, isLoading: isLoadingCategories } = useQuery<
    Category[]
  >({
    queryKey: [CATEGORIES_QUERY_KEY, businessId],
    queryFn: async () => {
      if (!businessId) return [];
      const url = `/categories/?business_id=${businessId}`;
      const response = await axios.get<Category[]>(url);
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!businessId,
  });

  const categories: Category[] = categoriesData ?? [];

  // --- Mutations ---

  // Create Category Mutation
  const { mutate: createCategory, isPending: isCreatingCategory } = useMutation<
    Category,
    Error,
    CreateCategoryInput // Expect Name and Description from caller
  >({
    mutationFn: async (newCategoryInput: CreateCategoryInput) => {
      if (!businessId) {
        throw new Error("Business ID is required to create a category.");
      }
      // Construct the full payload required by the API (Category schema)
      const payload: Category = {
        ...newCategoryInput, // contains name and description
        business_id: businessId, // Add business_id
        // id and created_at are omitted as they are not part of input/added by server
      };
      const response = await axios.post<Category>("/categories/", payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CATEGORIES_QUERY_KEY, businessId],
      });
    },
    onError: genericError,
  });

  // Update Category Mutation
  const { mutate: updateCategory, isPending: isUpdatingCategory } = useMutation<
    Category,
    Error,
    { categoryId: string; updateData: UpdateCategoryRequest }
  >({
    mutationFn: async ({ categoryId, updateData }) => {
      const response = await axios.put<Category>(
        `/categories/${categoryId}`,
        updateData
      );
      return response.data;
    },
    onSuccess: (_data, _variables) => {
      queryClient.invalidateQueries({
        queryKey: [CATEGORIES_QUERY_KEY, businessId],
      });
    },
    onError: genericError,
  });

  // Delete Category Mutation
  const { mutate: deleteCategory, isPending: isDeletingCategory } = useMutation<
    void,
    Error,
    string
  >({
    mutationFn: async (categoryId) => {
      await axios.delete(`/categories/${categoryId}`);
    },
    onSuccess: (_data, _categoryId) => {
      queryClient.invalidateQueries({
        queryKey: [CATEGORIES_QUERY_KEY, businessId],
      });
    },
    onError: genericError,
  });

  return {
    categories,
    isLoadingCategories,
    createCategory,
    isCreatingCategory,
    updateCategory,
    isUpdatingCategory,
    deleteCategory,
    isDeletingCategory,
  };
};
